import React from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
} from '@mui/material';

const PrivacyPolicy = () => {
  return (
    <Container maxWidth="lg" sx={{ my: { xs: 2, sm: 3, md: 4 } }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Privacy Policy
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Effective Date: January 1, 2025
        </Typography>
      </Box>

      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" sx={{ mt: 3, mb: 1 }}>
          1. Scope.
        </Typography>
        <Typography variant="body1" paragraph>
          This Privacy Policy applies to personal information processed by ESG Dynamics, LLC ("ESG Dynamics," "we," "us," and "our") in the course of our business, as collected from www.esg-dynamics.com and www.dilidesk.com (the "Sites") and from other websites and offline offerings that we operate (collectively, the "Services"). All capitalized terms not defined in this Privacy Policy will have the meanings set forth in the ESG Dynamics Terms of Use.
        </Typography>

        <Typography variant="h6" sx={{ mt: 3, mb: 1 }}>
          2. Personal Information We Collect.
        </Typography>
        <Typography variant="body1" paragraph>
          When you use our Services, we may collect the following categories of personal information:
        </Typography>

        <Typography variant="h6" sx={{ mt: 3, mb: 1 }}>
          2.1 Information You Provide to Us.
        </Typography>

        <Typography variant="body2" sx={{ mt: 2, mb: 1, fontWeight: 'bold' }}>
          2.1.1 Account Information.
        </Typography>
        <Typography variant="body1" paragraph>
          When you create an account, we will collect your name and email address. We may also collect your credit or debit card information, address, and phone number. Please note that we use a third-party service provider to collect and process payment card information on the Services. ESG Dynamics does not directly collect or store any payment card information provided to the Services, but it may receive information associated with your payment card information (e.g., your billing details).
        </Typography>

        <Typography variant="body2" sx={{ mt: 2, mb: 1, fontWeight: 'bold' }}>
          2.1.2 Communications with Us.
        </Typography>
        <Typography variant="body1" paragraph>
          We may collect personal information from you such as email address, phone number, or mailing address when you request information about our Services, sign up for our newsletter, request customer or technical support, or otherwise communicate with us.
        </Typography>

        <Typography variant="body2" sx={{ mt: 2, mb: 1, fontWeight: 'bold' }}>
          2.1.3 Customer Service and Support.
        </Typography>
        <Typography variant="body1" paragraph>
          If you call or otherwise interact with ESG Dynamics's customer service and support, we may collect the information you provide to our representatives. In addition, we may record telephone calls between you and our representatives for training and quality assurance purposes.
        </Typography>

        <Typography variant="body2" sx={{ mt: 2, mb: 1, fontWeight: 'bold' }}>
          2.1.4 Interactive Features.
        </Typography>
        <Typography variant="body1" paragraph>
          ESG Dynamics may offer interactive features such as commenting functionalities, collaboration tools, forums, chat services, and social media pages. ESG Dynamics and other individuals who use our Services may collect the information you submit or make available through these interactive features. Any information shared on the public sections of these channels will be considered "public" and will not be subject to the privacy protections referenced herein. By using these interactive features, you assume the risk that the personal information provided by you may be viewed and used by third parties for any number of purposes.
        </Typography>

        <Typography variant="body2" sx={{ mt: 2, mb: 1, fontWeight: 'bold' }}>
          2.1.5 Surveys.
        </Typography>
        <Typography variant="body1" paragraph>
          We may contact you to participate in surveys. If you decide to participate, you may be asked to provide certain information which may include personal information.
        </Typography>

        <Typography variant="body2" sx={{ mt: 2, mb: 1, fontWeight: 'bold' }}>
          2.1.6 Conferences, Trade Shows, and other Events.
        </Typography>
        <Typography variant="body1" paragraph>
          We may attend conferences, trade shows, and other events where we collect personal information from individuals who interact with or express an interest in ESG Dynamics and/or the Services. If you provide us with any information at one of these events, we will use it for the purposes for which it was collected.
        </Typography>

        <Typography variant="body2" sx={{ mt: 2, mb: 1, fontWeight: 'bold' }}>
          2.1.7 Business Development and Strategic Partnerships.
        </Typography>
        <Typography variant="body1" paragraph>
          We may collect personal information from individuals and third parties to assess and pursue potential business opportunities.
        </Typography>

        <Typography variant="h6" sx={{ mt: 3, mb: 1 }}>
          2.2 Information We Collect Through Your Use of the Services.
        </Typography>
        <Typography variant="body1" paragraph>
          We may collect certain information automatically when you use the Services. This information may include your Internet protocol (IP) address, user settings, MAC address, cookie identifiers, mobile carrier, mobile advertising and other unique identifiers, details about your browser, operating system or device, location information (including inferred location based off of your IP address), internet service provider, pages that you visit before, during and after using the Services, information about the links you click, information about how you interact with the Services, including the frequency and duration of your activities, and other information about how you use the Services. Information we collect may be associated with accounts and other devices.
        </Typography>

        <Typography variant="h6" sx={{ mt: 3, mb: 1 }}>
          2.3 Information from Other Sources.
        </Typography>
        <Typography variant="body1" paragraph>
          <strong>Our Customers.</strong> If you use our Services on behalf of an organization (e.g., your employer), that organization may provide us with information about you so that we can provision your account.
        </Typography>
        <Typography variant="body1" paragraph>
          <strong>Third Party Services and Organizations.</strong> We may obtain information about you from other sources, including through third-party services and other organizations. For example, if you access or use our Services through a third-party application, such as a third-party login service, we may collect information about you from that third party that you have made available via your privacy settings.
        </Typography>

        <Typography variant="h6" sx={{ mt: 3, mb: 1 }}>
          3. How We Use Your Information.
        </Typography>
        <Typography variant="body1" paragraph>
          We process personal information for a variety of business purposes, including:
        </Typography>

        <Typography variant="body2" sx={{ mt: 2, mb: 1, fontWeight: 'bold' }}>
          3.1 To provide the Services or Information Requested.
        </Typography>
        <Box component="ol" sx={{ pl: 3 }}>
          <Typography component="li" variant="body1" paragraph>
            Fulfill our contract with you or the organization on whose behalf you use the Services;
          </Typography>
          <Typography component="li" variant="body1" paragraph>
            Assess the needs of your business to determine suitable products;
          </Typography>
          <Typography component="li" variant="body1" paragraph>
            Manage your information;
          </Typography>
          <Typography component="li" variant="body1" paragraph>
            Process payment card and/or other financial information to facilitate your use of the Services;
          </Typography>
          <Typography component="li" variant="body1" paragraph>
            Respond to questions, comments, and other requests;
          </Typography>
          <Typography component="li" variant="body1" paragraph>
            Provide access to certain areas, functionalities, and features of our Services; and
          </Typography>
          <Typography component="li" variant="body1" paragraph>
            Answer requests for customer or technical support.
          </Typography>
        </Box>

        <Typography variant="body2" sx={{ mt: 2, mb: 1, fontWeight: 'bold' }}>
          3.2 Administrative Purposes.
        </Typography>
        <Box component="ol" sx={{ pl: 3 }}>
          <Typography component="li" variant="body1" paragraph>
            Pursue legitimate interests, such as direct marketing, research and development (including marketing research), network and information security, and fraud prevention;
          </Typography>
          <Typography component="li" variant="body1" paragraph>
            Measure interest and engagement in the Services;
          </Typography>
          <Typography component="li" variant="body1" paragraph>
            Develop of new products and services;
          </Typography>
          <Typography component="li" variant="body1" paragraph>
            Improve our products and Services;
          </Typography>
          <Typography component="li" variant="body1" paragraph>
            Ensure internal quality control and safety;
          </Typography>
          <Typography component="li" variant="body1" paragraph>
            Verify individual identity;
          </Typography>
          <Typography component="li" variant="body1" paragraph>
            Carry out audits;
          </Typography>
          <Typography component="li" variant="body1" paragraph>
            Communicate with you about activities on the Services and changes to our agreements;
          </Typography>
          <Typography component="li" variant="body1" paragraph>
            Prevent and prosecute potentially prohibited or illegal activities;
          </Typography>
          <Typography component="li" variant="body1" paragraph>
            Enforce our agreements; and
          </Typography>
          <Typography component="li" variant="body1" paragraph>
            Comply with our legal obligations.
          </Typography>
        </Box>

        <Typography variant="body2" sx={{ mt: 2, mb: 1, fontWeight: 'bold' }}>
          3.3 Marketing Our Products and Services.
        </Typography>
        <Typography variant="body1" paragraph>
          We may use personal information to tailor and provide you with content and advertisements. We may provide you with these materials as permitted by applicable law. If you have any questions about our marketing practices or if you would like to opt out of the use of your personal information for marketing purposes, you may contact us at any time as set forth below.
        </Typography>

        <Typography variant="body2" sx={{ mt: 2, mb: 1, fontWeight: 'bold' }}>
          3.4 De-identified and Aggregated Information Use.
        </Typography>
        <Typography variant="body1" paragraph>
          We may use personal information and other information about you to create de-identified and/or aggregated information, such as de-identified demographic information, de-identified location information, de-identified or aggregated trends, reports, or statistics, or other analyses we create. De-identified and aggregated information is not personal information, and we may use and disclose such information in a number of ways, including research, internal analysis, analytics, and any other legally permissible purposes.
        </Typography>

        <Typography variant="h6" sx={{ mt: 3, mb: 1 }}>
          4. How We Disclose Your Information.
        </Typography>
        <Typography variant="body1" paragraph>
          <strong>Your Organization.</strong> If you use our Services on behalf of an organization (e.g., your employer), that organization may: (i) access information associated with your use of the Services including usage and other data (e.g., who has accessed, shared, amended, created, edited, or deleted Documents), and the contents of the communications and files associated with your account; and (ii) control and administer your account.
        </Typography>
        <Typography variant="body1" paragraph>
          <strong>Service Providers.</strong> We may share any personal information we collect about you with our third-party service providers. The types of service providers to whom we entrust personal information include service providers for: (i) the provision of the Services; (ii) the provision of information, products, and other services you have requested; (iii) marketing and advertising; (iv) payment processing; (v) customer service activities; and (vi) the provision IT and related services.
        </Typography>
        <Typography variant="body1" paragraph>
          <strong>Affiliates.</strong> We may share personal information with our affiliated entities.
        </Typography>
        <Typography variant="body1" paragraph>
          <strong>Business Partners.</strong> We may provide personal information to business partners to provide you with a product or service you have requested. We may also provide personal information to business partners with whom we jointly offer products or services.
        </Typography>

        <Typography variant="h6" sx={{ mt: 3, mb: 1 }}>
          5. International Data Transfers.
        </Typography>
        <Typography variant="body1" paragraph>
          All information processed by us may be transferred, processed, and stored anywhere in the world, including but not limited to, the United States and other countries.
        </Typography>

        <Typography variant="h6" sx={{ mt: 3, mb: 1 }}>
          6. Your Choices.
        </Typography>
        <Typography variant="body1" paragraph>
          <strong>General.</strong> You may have the right to object to or opt out of certain uses of your personal information.
        </Typography>
        <Typography variant="body1" paragraph>
          <strong>Email Communications.</strong> If you receive an unwanted email from us, you can use the unsubscribe link found at the bottom of the email. Note that you will continue to receive transaction-related emails regarding products or Services you have requested. We may also send you certain non-promotional communications regarding us and our Services, and you will not be able to opt out of those communications (e.g., communications regarding the Services or updates to this Privacy Policy).
        </Typography>

        <Typography variant="h6" sx={{ mt: 3, mb: 1 }}>
          7. Individual Rights in Personal Information.
        </Typography>
        <Typography variant="body1" paragraph>
          For purposes of the California Consumer Privacy Act, ESG Dynamics does not "sell" your personal information.
        </Typography>
        <Typography variant="body1" paragraph>
          In accordance with applicable law, you may have the right to: (i) request confirmation of whether we are processing your personal information; (ii) obtain access to or a copy of your personal information; (iii) receive an electronic copy of personal information that you have provided to us, or ask us to send that information to another company (the "right of data portability"); (iv) restrict our uses of your personal information; (v) seek correction or amendment of inaccurate, untrue, incomplete, or improperly processed personal information; (vi) withdraw your consent; and (vii) request erasure of personal information held about you by ESG Dynamics, subject to certain exceptions prescribed by law. If you would like to exercise any of these rights, please contact us as set forth below.
        </Typography>
        <Typography variant="body1" paragraph>
          We will process such requests in accordance with applicable laws. To protect your privacy, ESG Dynamics will take steps to verify your identity before fulfilling your request.
        </Typography>
        <Typography variant="body1" paragraph>
          To request a copy of the specific pieces of personal information ESG Dynamics has collected about you over the preceding 12 months, please contact ESG Dynamics at info@esg-dynamics.com.
        </Typography>
        <Typography variant="body1" paragraph>
          To request deletion of the personal information ESG Dynamics has collected about you, please contact ESG Dynamics at info@esg-dynamics.com.
        </Typography>

        <Typography variant="h6" sx={{ mt: 3, mb: 1 }}>
          8. Data Retention.
        </Typography>
        <Typography variant="body1" paragraph>
          We retain the personal information we receive as described in this Privacy Policy for as long as you use our Services or as necessary to fulfill the purpose(s) for which it was collected, provide our Services, resolve disputes, establish legal defenses, conduct audits, pursue legitimate business purposes, enforce our agreements, and comply with applicable laws.
        </Typography>

        <Typography variant="h6" sx={{ mt: 3, mb: 1 }}>
          9. Security of Your Information.
        </Typography>
        <Typography variant="body1" paragraph>
          We take steps to ensure that your information is treated securely and in accordance with this Privacy Policy. Unfortunately, the Internet cannot be guaranteed to be 100% secure, and we cannot ensure or warrant the security of any information you provide to us. To the fullest extent permitted by applicable law, we do not accept liability for unauthorized disclosure.
        </Typography>
        <Typography variant="body1" paragraph>
          By using the Services or providing personal information to us, you agree that we may communicate with you electronically regarding security, privacy, and administrative issues relating to your use of the Services. If we learn of a security system's breach, we may attempt to notify you electronically by sending a notice through the Services or by sending an e-mail to you.
        </Typography>

        <Typography variant="h6" sx={{ mt: 3, mb: 1 }}>
          10. Third Party Websites/Applications.
        </Typography>
        <Typography variant="body1" paragraph>
          The Services may contain links to other websites/applications and other websites/applications may reference or link to our Services. These third-party services are not controlled by us. We encourage our users to read the privacy policies of each website and application with which they interact. We do not endorse, screen or approve, and are not responsible for the privacy practices or content of such other websites or applications. Visiting these other websites or applications is at your own risk.
        </Typography>

        <Typography variant="h6" sx={{ mt: 3, mb: 1 }}>
          11. Children's Information.
        </Typography>
        <Typography variant="body1" paragraph>
          The Services are not directed to children under 13 (or other age as required by local law), and we do not knowingly collect personal information from children. If you learn that your child has provided us with personal information without your consent, you may contact us as set forth below. If we learn that we have collected any child's personal information in violation of applicable law, we will promptly take steps to delete such information.
        </Typography>

        <Typography variant="h6" sx={{ mt: 3, mb: 1 }}>
          12. Changes to Our Privacy Policy.
        </Typography>
        <Typography variant="body1" paragraph>
          We may revise this Privacy Policy from time to time in our sole discretion. If there are any material changes to this Privacy Policy, we will post a notice of the update on our Sites, or as otherwise required by applicable law. You understand and agree that you will be deemed to have accepted the updated Privacy Policy if you continue to use the Services after the new Privacy Policy takes effect.
        </Typography>

        <Box sx={{ mt: 4, p: 2, backgroundColor: 'grey.50', borderRadius: 1 }}>
          <Typography variant="h6" sx={{ mb: 1 }}>
            Contact Us
          </Typography>
          <Typography variant="body1">
            ESG Dynamics, LLC<br />
            PO BOX 141747<br />
            Irving, TX 75014, United States<br />
            <br />
            info@esg-dynamics.com
          </Typography>
        </Box>
      </Paper>
    </Container>
  );
};

export default PrivacyPolicy;
